import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Service, ServiceSection } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';
import RichTextEditor from '../../components/RichTextEditor';
import { GoogleGenAI } from '@google/genai';

const generateSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const SparklesIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8-5.8 1.9 5.8 1.9 1.9 5.8 1.9-5.8 5.8-1.9-5.8-1.9z"/></svg>;


const AdminServiceFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [generatingIndex, setGeneratingIndex] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
    const [imagePrompt, setImagePrompt] = useState('');
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [generatedImage, setGeneratedImage] = useState<string | null>(null); // base64 string
    
    const [service, setService] = useState<Omit<Service, 'id'>>({
        title: '',
        slug: '',
        imageUrl: '',
        description: '',
        sections: [{ title: 'Overview', content: '' }],
        order: 0,
    });

    useEffect(() => {
        const fetchService = async () => {
            if (isEditing && id) {
                const docRef = doc(db, 'services', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setService(docSnap.data() as Service);
                } else {
                    showToast('Service not found.', { type: 'error' });
                    navigate('/admin/services');
                }
            } else {
                const snapshot = await getDocs(collection(db, 'services'));
                setService(prev => ({ ...prev, order: snapshot.size }));
            }
            setIsLoading(false);
        };
        fetchService();
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setService(prev => ({ ...prev, [name]: value }));
        if (name === 'title' && !isEditing) {
            setService(prev => ({ ...prev, slug: generateSlug(value) }));
        }
    };
    
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `services/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                setService(prev => ({ ...prev, imageUrl: downloadURL }));
            } catch (error) {
                showToast('Image upload failed.', { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    const handleSectionChange = (index: number, field: keyof ServiceSection, value: string) => {
        const newSections = [...service.sections];
        newSections[index] = { ...newSections[index], [field]: value };
        setService(prev => ({ ...prev, sections: newSections }));
    };

    const addSection = () => setService(prev => ({ ...prev, sections: [...prev.sections, { title: 'New Section', content: '' }] }));
    const removeSection = (index: number) => setService(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== index) }));

    const handleGenerateDescription = async () => {
        const promptText = service.description.trim() || service.title;
        if (!promptText) {
            showToast("Please provide a title or some keywords in the description to improve.", { type: 'error' });
            return;
        }
        setIsGeneratingDesc(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const prompt = `As a professional copywriter for an architectural firm, rewrite or expand upon the following idea into a compelling, short service description (under 40 words). The idea is: "${promptText}". The tone should be professional, client-focused, and clear.`;
            
            const response = await ai.models.generateContent({
                // FIX: Update model to recommended model for text generation.
                model: 'gemini-3-flash-preview',
                contents: prompt,
            });
            const text = response.text.trim();
            if (text) {
                setService(prev => ({ ...prev, description: text }));
                showToast('Description improved with AI!', { type: 'success' });
            } else {
                throw new Error("AI did not return any text.");
            }
        } catch (error) {
            console.error("AI description generation failed:", error);
            showToast("Sorry, there was an error generating the description.", { type: 'error' });
        } finally {
            setIsGeneratingDesc(false);
        }
    };

     const handleGenerateContent = async (index: number) => {
        const section = service.sections[index];
        const promptText = section.content.replace(/<[^>]*>?/gm, '').trim() || section.title;
        if (!promptText) {
            showToast("Provide a title or content to improve.", { type: 'error' });
            return;
        }
        setGeneratingIndex(index);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const prompt = `As a professional copywriter for an architectural firm, expand upon or rewrite the following idea for a service description section. The idea is: "${promptText}". The response should be one or two paragraphs wrapped in <p> tags. The tone must be professional and client-focused.`;
            // FIX: Update model to recommended model for text generation.
            const response = await ai.models.generateContent({ model: 'gemini-3-flash-preview', contents: prompt });
            const text = response.text.trim();
            if (text) handleSectionChange(index, 'content', text);
        } catch (error) {
            showToast("Failed to generate content.", { type: 'error' });
        } finally {
            setGeneratingIndex(null);
        }
    };
    
    const handleGenerateImage = async () => {
        if (!imagePrompt.trim()) {
            showToast("Please enter a prompt to generate an image.", { type: 'error' });
            return;
        }
        setIsGeneratingImage(true);
        setGeneratedImage(null);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const response = await ai.models.generateImages({
                model: 'imagen-4.0-generate-001',
                prompt: `Professional architectural photography of ${imagePrompt}. High-end, minimalist, clean lighting.`,
                config: {
                    numberOfImages: 1,
                    outputMimeType: 'image/jpeg',
                    aspectRatio: '4:3',
                },
            });
            const base64ImageBytes = response.generatedImages[0].image.imageBytes;
            setGeneratedImage(base64ImageBytes);
            showToast("Image generated successfully!", { type: 'success' });
        } catch (error) {
            console.error("AI image generation failed:", error);
            showToast("Sorry, there was an error generating the image.", { type: 'error' });
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const handleUseGeneratedImage = async () => {
        if (!generatedImage) return;
        setIsUploading(true);
        try {
            const byteCharacters = atob(generatedImage);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            const storageRef = ref(storage, `services/ai_generated_${Date.now()}.jpeg`);
            await uploadBytes(storageRef, blob);
            const downloadURL = await getDownloadURL(storageRef);
            setService(prev => ({ ...prev, imageUrl: downloadURL }));
            setGeneratedImage(null);
            showToast("AI image has been set as the service image.", { type: 'success' });
        } catch (error) {
            showToast("Failed to save the generated image.", { type: 'error' });
        } finally {
            setIsUploading(false);
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const dataToSave = { ...service, slug: service.slug || generateSlug(service.title) };
            if (isEditing && id) {
                await updateDoc(doc(db, 'services', id), dataToSave);
                showToast('Service updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'services'), dataToSave);
                showToast('Service created successfully!', { type: 'success' });
            }
            navigate('/admin/services');
        } catch (error) {
            showToast('Failed to save service.', { type: 'error' });
            setIsSaving(false);
        }
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Service' : 'Add New Service'}</h2>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="bg-white p-8 rounded-lg shadow-md">
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                         <div className="md:col-span-2 space-y-4">
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700">Service Title</label>
                                <input type="text" name="title" id="title" value={service.title} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                            </div>
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Short Description</label>
                                 <div className="relative">
                                    <textarea name="description" id="description" rows={3} value={service.description} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
                                    <button type="button" onClick={handleGenerateDescription} disabled={isGeneratingDesc} className="absolute top-2.5 right-1.5 p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-50" title="Improve with AI">
                                        <SparklesIcon className={`h-5 w-5 ${isGeneratingDesc ? 'animate-pulse text-indigo-600' : ''}`} />
                                    </button>
                                </div>
                            </div>
                         </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Service Image</label>
                            <div className="relative w-full h-40 border-2 border-dashed rounded-md flex items-center justify-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                {(isUploading && !generatedImage) && <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10"><div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div></div>}
                                {service.imageUrl ? <img src={service.imageUrl} alt="Header" className="h-full w-full object-contain p-2 rounded-md"/> : <div className="text-center text-gray-500"><UploadIcon className="mx-auto h-8 w-8"/><span className="mt-2 text-sm">Upload Image</span></div>}
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*"/>
                            </div>
                             <div className="mt-4 border-t pt-4">
                                <label htmlFor="imagePrompt" className="block text-xs font-medium text-gray-700">Or Generate with AI</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <input type="text" id="imagePrompt" value={imagePrompt} onChange={e => setImagePrompt(e.target.value)} placeholder="e.g., modern building facade" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"/>
                                    <button type="button" onClick={handleGenerateImage} disabled={isGeneratingImage} className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 flex items-center whitespace-nowrap text-sm">
                                        <SparklesIcon className={`h-4 w-4 mr-1 ${isGeneratingImage ? 'animate-pulse' : ''}`} />
                                        {isGeneratingImage ? '...' : 'Generate'}
                                    </button>
                                </div>
                                {isGeneratingImage && <p className="text-xs text-gray-500 mt-2">AI is creating your image. This may take a moment...</p>}
                                {generatedImage && (
                                    <div className="mt-2">
                                        <img src={`data:image/jpeg;base64,${generatedImage}`} alt="AI generated" className="w-full h-auto rounded-md border" />
                                        <button type="button" onClick={handleUseGeneratedImage} disabled={isUploading} className="mt-2 w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 text-sm">
                                            {isUploading ? 'Saving...' : 'Use This Image'}
                                        </button>
                                    </div>
                                )}
                            </div>
                         </div>
                     </div>
                </div>

                <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Content Sections</h3>
                    <div className="space-y-6">
                        {service.sections.map((section, index) => (
                            <div key={index} className="border p-4 rounded-md relative bg-gray-50">
                                <button type="button" onClick={() => removeSection(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600 font-bold text-xl z-10">&times;</button>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700">Section Title</label>
                                    <input type="text" value={section.title} onChange={(e) => handleSectionChange(index, 'title', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Content</label>
                                     <div className="relative">
                                        <RichTextEditor value={section.content} onChange={(value) => handleSectionChange(index, 'content', value)} />
                                        <button type="button" onClick={() => handleGenerateContent(index)} disabled={generatingIndex === index} className="absolute top-2 right-12 z-10 p-1.5 text-gray-500 hover:text-indigo-600 disabled:opacity-50" title="Improve with AI">
                                            <SparklesIcon className={`h-5 w-5 ${generatingIndex === index ? 'animate-pulse text-indigo-600' : ''}`} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button type="button" onClick={addSection} className="mt-6 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Add Section</button>
                </div>
                <div className="flex justify-end space-x-4">
                    <button type="button" onClick={() => navigate('/admin/services')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                    <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Service'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminServiceFormPage;